import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MapPin, FileText, DollarSign, Settings2 } from 'lucide-react';
import type { EarningsParams } from '@/types/technicianRoutes';
import {
  computeRouteEarnings,
  computePerRecallValue,
  formatCurrency,
  resolveEarningsParams
} from '../utils/earnings';

interface RoutePreviewStats {
  totalDealers: number;
  totalRecalls: number;
  totalVehicles: number;
  estimatedDuration: number;
  /** Number of technician routes on the board (= van count for the earnings projection). */
  totalRoutes: number;
}

interface RoutePreviewHeaderProps {
  stats: RoutePreviewStats;
  /** Resolved earnings params (tenant-configured or defaults). */
  earningsParams?: EarningsParams;
  /** Persist edited earnings params (tenant-wide). Omit to make the card read-only. */
  onSaveEarningsParams?: (params: EarningsParams) => void;
}

/** Editable Est. Route Earnings card with an inline assumptions popover. */
const EarningsCard: React.FC<{
  /** Actual number of recalls planned for today's route. */
  totalRecalls: number;
  params: EarningsParams;
  onSave?: (params: EarningsParams) => void;
}> = ({ totalRecalls, params, onSave }) => {
  const [open, setOpen] = useState(false);
  // Local draft values (as strings so inputs can be cleared while typing)
  const [laborRate, setLaborRate] = useState(String(params.labor_rate_per_hour));
  const [hoursPerRecall, setHoursPerRecall] = useState(String(params.hours_per_recall));
  const [grossPercent, setGrossPercent] = useState(
    String(Math.round(params.gross_percentage * 100))
  );
  const [offsetRate, setOffsetRate] = useState(String(params.offset_rate_per_recall));

  // Re-seed drafts whenever the popover opens or the saved params change
  useEffect(() => {
    if (open) {
      setLaborRate(String(params.labor_rate_per_hour));
      setHoursPerRecall(String(params.hours_per_recall));
      setGrossPercent(String(Math.round(params.gross_percentage * 100)));
      setOffsetRate(String(params.offset_rate_per_recall));
    }
  }, [open, params]);

  const routeTotal = computeRouteEarnings(totalRecalls, params);

  // Live preview using in-progress draft values.
  const draftParams = {
    ...params,
    labor_rate_per_hour: Number(laborRate) || 0,
    hours_per_recall: Number(hoursPerRecall) || 0,
    gross_percentage: (Number(grossPercent) || 0) / 100,
    offset_rate_per_recall: Number(offsetRate) || 0
  };
  const draftPerRecall = computePerRecallValue(draftParams);
  const draftTotal = computeRouteEarnings(totalRecalls, draftParams);

  const handleSave = () => {
    if (!onSave) return;
    // Preserve non-edited params (daily_ros_per_van, working_days_per_month, parts
    // percentages) via spread; only overwrite the fields shown in the popover.
    const next: EarningsParams = {
      ...params,
      labor_rate_per_hour: Number(laborRate) || 0,
      hours_per_recall: Number(hoursPerRecall) || 0,
      gross_percentage: (Number(grossPercent) || 0) / 100,
      offset_rate_per_recall: Number(offsetRate) || 0
    };
    onSave(next);
    setOpen(false);
  };

  return (
    <Card>
      <CardContent className='p-4'>
        <div className='flex items-center justify-between'>
          <div>
            <div className='flex items-center gap-1.5'>
              <p className='text-sm font-medium text-muted-foreground'>Est. Route Earnings</p>
              {onSave && (
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type='button'
                      className='text-muted-foreground/70 hover:text-foreground'
                      title='Edit earnings assumptions'
                    >
                      <Settings2 className='h-3.5 w-3.5' />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align='end' className='w-72 space-y-3'>
                    <div>
                      <h4 className='text-sm font-semibold text-foreground'>
                        Earnings assumptions
                      </h4>
                      <p className='text-xs text-muted-foreground'>
                        Applies to all routes for your dealership.
                      </p>
                    </div>
                    <div className='space-y-2'>
                      <div className='space-y-1'>
                        <Label htmlFor='labor-rate' className='text-xs'>
                          Labor rate ($/hour)
                        </Label>
                        <Input
                          id='labor-rate'
                          type='number'
                          min='0'
                          step='1'
                          value={laborRate}
                          onChange={e => setLaborRate(e.target.value)}
                          className='h-8'
                        />
                      </div>
                      <div className='space-y-1'>
                        <Label htmlFor='hours-per-recall' className='text-xs'>
                          Hours per recall
                        </Label>
                        <Input
                          id='hours-per-recall'
                          type='number'
                          min='0'
                          step='0.05'
                          value={hoursPerRecall}
                          onChange={e => setHoursPerRecall(e.target.value)}
                          className='h-8'
                        />
                      </div>
                      <div className='space-y-1'>
                        <Label htmlFor='gross-percent' className='text-xs'>
                          Gross share (%)
                        </Label>
                        <Input
                          id='gross-percent'
                          type='number'
                          min='0'
                          max='100'
                          step='1'
                          value={grossPercent}
                          onChange={e => setGrossPercent(e.target.value)}
                          className='h-8'
                        />
                      </div>
                      <div className='space-y-1'>
                        <Label htmlFor='offset-rate' className='text-xs'>
                          Manufacturer offset ($/RO)
                        </Label>
                        <Input
                          id='offset-rate'
                          type='number'
                          min='0'
                          step='1'
                          value={offsetRate}
                          onChange={e => setOffsetRate(e.target.value)}
                          className='h-8'
                        />
                      </div>
                    </div>
                    <p className='rounded-md bg-muted px-2 py-1.5 text-[11px] text-muted-foreground'>
                      {totalRecalls} {totalRecalls === 1 ? 'recall' : 'recalls'} ×{' '}
                      {formatCurrency(draftPerRecall)}/recall ={' '}
                      <span className='font-semibold text-foreground'>
                        {formatCurrency(draftTotal)}
                      </span>
                    </p>
                    <div className='flex justify-end gap-2'>
                      <Button variant='ghost' size='sm' onClick={() => setOpen(false)}>
                        Cancel
                      </Button>
                      <Button size='sm' onClick={handleSave}>
                        Save
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <p className='text-2xl font-bold text-foreground'>{formatCurrency(routeTotal)}</p>
            <p className='text-xs text-muted-foreground'>
              {totalRecalls} {totalRecalls === 1 ? 'recall' : 'recalls'} planned
            </p>
          </div>
          <DollarSign className='w-8 h-8 text-emerald-500 dark:text-emerald-400' />
        </div>
      </CardContent>
    </Card>
  );
};

const RoutePreviewHeader: React.FC<RoutePreviewHeaderProps> = ({
  stats,
  earningsParams,
  onSaveEarningsParams
}) => {
  const resolvedParams = resolveEarningsParams(earningsParams);

  return (
    <div className='space-y-4'>
      {/* Stats Cards */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-muted-foreground'>Total Dealers</p>
                <p className='text-2xl font-bold text-foreground'>{stats.totalDealers}</p>
              </div>
              <MapPin className='w-8 h-8 text-blue-500 dark:text-blue-400' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-muted-foreground'>Total Recalls</p>
                <p className='text-2xl font-bold text-foreground'>{stats.totalRecalls}</p>
              </div>
              <FileText className='w-8 h-8 text-purple-500 dark:text-purple-400' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-muted-foreground'>Total Vehicles</p>
                <p className='text-2xl font-bold text-foreground'>{stats.totalVehicles}</p>
              </div>
              <div className='w-8 h-8 text-green-500 dark:text-green-400 flex items-center justify-center'>
                <svg className='w-8 h-8' fill='currentColor' viewBox='0 0 24 24'>
                  {/* eslint-disable-next-line max-len */}
                  <path d='M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z' />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <EarningsCard
          totalRecalls={stats.totalRecalls}
          params={resolvedParams}
          {...(onSaveEarningsParams && { onSave: onSaveEarningsParams })}
        />
      </div>
    </div>
  );
};

export default RoutePreviewHeader;
